/* eslint-disable no-bitwise */
/**
 * Checks if the browser supports the Web Crypto API
 * @returns {boolean} True if Web Crypto API is supported
 */
const isWebCryptoSupported = (): boolean =>
  typeof window !== 'undefined' &&
  window.crypto !== undefined &&
  window.crypto.subtle !== undefined;

/**
 * Simple fallback hash function for browsers that don't support Web Crypto API
 * @param {string} input - The string to hash
 * @returns {string} A basic hash of the input string
 */
const fallbackHash = (input: string): string => {
  let hash = 0;
  for (let i = 0; i < input.length; i += 1) {
    const char = input.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash &= hash; // Convert to 32-bit integer
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
};

/**
 * Generates a SHA-256 hash of the input string
 * @param {string} input - The string to hash
 * @returns {Promise<string>} The hexadecimal representation of the hash
 * @throws {Error} If neither Web Crypto API is supported nor fallback is available
 *
 * @example
 * // Basic string hashing
 * const hash = await generateSHA256Hash('simple-string');
 *
 * // For objects, serialize them first:
 * const obj = { a: 1, b: 2 };
 * const serialized = JSON.stringify(obj, Object.keys(obj).sort());
 * const hash = await generateSHA256Hash(serialized);
 */
const generateSHA256Hash = async (input: string): Promise<string> => {
  if (isWebCryptoSupported()) {
    try {
      // Convert the input string to a Uint8Array
      const encoder = new TextEncoder();
      const data = encoder.encode(input);

      // Generate the hash using the Web Crypto API
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);

      // Convert the hash buffer to a hexadecimal string
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch (error) {
      console.warn('Web Crypto API failed, falling back to basic hash:', error);
      return fallbackHash(input);
    }
  }

  console.warn(
    'Web Crypto API not supported, using fallback hash function. This is NOT cryptographically secure!',
  );
  return fallbackHash(input);
};

export default generateSHA256Hash;

/**
 * Example usage:
 * // With string
 * const stringHash = await generateSHA256Hash('your-string-to-hash');
 *
 * // With object
 * const objectHash = await generateSHA256Hash({
 *   userId: '123',
 *   action: 'create',
 *   timestamp: '2024-03-20'
 * });
 */
